import { useEffect, useRef } from 'react';
import { DefaultTheme, NavigationContainer } from '@react-navigation/native';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import linking from 'src/presentation/navigation/LinkingConfiguration';
import OrderBathCardDialog from 'src/presentation/dialogs/OrderBathCardDialog';
import ReservationBarcodeDialog from 'src/presentation/dialogs/ReservationBarcodeDialog';
import { navigationRef } from 'src/presentation/navigation/navigationRef';
import RootNavigator from 'src/presentation/navigation/navigators/RootNavigator';
import WebBootHelper from 'src/presentation/helpers/WebBootHelper';

const theme: typeof DefaultTheme = {
	...DefaultTheme,
	colors: {
		...DefaultTheme.colors,
		background: Colors.gray75,
	},
};

export default function Navigation() {
	const routeNameRef = useRef<string | null>(null);
	const { user, forceLogin, setForceLogin } = useAuthStore();
	const { logEvent, logScreenView } = useServicesStore();

	useAuthStore((state) => state.language);

	useEffect(() => {
		if (!forceLogin || !navigationRef.isReady()) {
			return;
		}

		if (navigationRef.getCurrentRoute()?.name !== Constants.screenLogin) {
			navigationRef.reset({
				index: 0,
				routes: [{ name: Constants.navigatorAuth, state: { routes: [{ name: Constants.screenLogin }] } }],
			});
		}

		setForceLogin(false);
	}, [forceLogin, setForceLogin]);

	// page_view stays next to screen_view, so the reports built on it keep their history.
	const logScreen = (routeName: string | null) => {
		logEvent({
			name: Constants.analyticsEventPageView,
			params: {
				[Constants.analyticsParamScreenPage]: routeName,
			},
			user,
		});

		if (routeName !== null) {
			logScreenView({ screenName: routeName, user });
		}
	};

	return (
		<NavigationContainer
			linking={linking}
			ref={navigationRef}
			theme={theme}
			onReady={() => {
				routeNameRef.current = navigationRef.getCurrentRoute()?.name ?? null;
				// The login screen hides it itself, once it has measured its image region.
				if (routeNameRef.current !== Constants.screenLogin) {
					WebBootHelper.hideSplash();
				}
				logScreen(routeNameRef.current);
			}}
			onStateChange={() => {
				const previousRouteName = routeNameRef.current;
				const currentRouteName = navigationRef.getCurrentRoute()?.name ?? null;

				if (previousRouteName !== currentRouteName) {
					logScreen(currentRouteName);
				}

				routeNameRef.current = currentRouteName;
			}}
		>
			<RootNavigator />

			{/* Three screens open this sheet, and every one of them stays mounted in its tab, so it
			hangs here: one instance, still inside the container its actions read navigation from. */}
			<OrderBathCardDialog />

			{/* The account and the booking tab each keep a reservation detail mounted, and one store flag
			opens the barcode, so a sheet per screen means two sheets over each other. */}
			<ReservationBarcodeDialog />
		</NavigationContainer>
	);
}
